import { CashflowProvider } from '@/components/cashflow/CashflowContext';
import { LabelShape } from '@/components/common/charts/ChartTooltip';
import { FlexRow } from '@/components/common/Flex';
import { LayersThree02, Toggle03Left } from '@/components/common/icons';
import { BigHeader, Paragraph, Text } from '@/components/common/Typography';
import DashboardMoneyInPlentyChart from '@/components/dashboard/DashboardMoneyInPlentyChart';
import DashboardNetWorthChart from '@/components/dashboard/DashboardNetWorthChart';
import DashboardSpendingWidget from '@/components/dashboard/DashboardSpendingWidget';
import FtuOverlay from '@/components/ftu/FtuOverlay';
import { Grid } from '@chakra-ui/react';
import { useHasNoFEs } from '@common/react/financialEntities';
import { AuthedPageProviders } from '../AuthedPageContext';
import { FlexCol } from '../common/Flex';
import LeftNavPageLayout from '../common/pageLayout/LeftNavPageLayout';
import { PageContextProvider } from '../common/pageLayout/PageContext';
import { FinancialEntitiesProvider } from '../financialEntities/FinancialEntitiesProvider';
import useAcceptInviteModal from '../partnershipInvites/useAcceptInviteModal';
import DashboardAccounts from './DashboardAccounts';
import DashboardCarousel from './DashboardCarousel';
import { DashboardProvider, useDashboardContext } from './DashboardContext';
import DashboardRelinkSection from './DashboardRelinkSection';
import DashboardTitleSection from './DashboardTitleSection';
import DashboardTransactionsWidget from './DashboardTransactionsWidget';

const DashboardPageWrapper = () => {
    return (
        <PageContextProvider>
            <FinancialEntitiesProvider>
                <AuthedPageProviders>
                    <LeftNavPageLayout centerProps={{ maxW: 1200 }}>
                        <CashflowProvider>
                            <DashboardProvider>
                                <DashboardPage />
                            </DashboardProvider>
                        </CashflowProvider>
                    </LeftNavPageLayout>
                </AuthedPageProviders>
            </FinancialEntitiesProvider>
        </PageContextProvider>
    );
};

const CONNECT_ACCOUNT_COLOR = '#5CBC3D';

const DashboardPage = () => {
    // "Show-on-pageload" modals if conditions are met
    // TODO: Refactor to use a single hook for all modals, handle multi-trigger
    // situations gracefully
    useAcceptInviteModal();

    const { chartType } = useDashboardContext();

    const hasNoFEs = useHasNoFEs();

    return (
        <>
            <DashboardTitleSection />
            {chartType === 'in-plenty' ? (
                <DashboardMoneyInPlentyChart />
            ) : (
                <DashboardNetWorthChart />
            )}
            <DashboardCarousel />
            <DashboardRelinkSection />
            <Grid
                templateRows="auto"
                templateColumns={{
                    base: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                }}
                gap={{ base: 8, lg: '8%' }}
                w="full"
                mt={8}
            >
                <DashboardAccounts pt={5} />
                <FlexCol gap={4}>
                    <DashboardSpendingWidget />
                    <DashboardTransactionsWidget />
                </FlexCol>
            </Grid>
            {hasNoFEs && (
                <FtuOverlay
                    ctaText="Connect a bank account"
                    title={
                        <>
                            <BigHeader mb={6}>
                                Grow wealthy,
                                <br />
                                <FlexRow align="center">
                                    <LabelShape
                                        color={CONNECT_ACCOUNT_COLOR}
                                        shape="circle"
                                    />{' '}
                                    together.
                                </FlexRow>
                            </BigHeader>
                            <BigHeader mb={4}>
                                Finally,
                                <br />
                                a money app
                                <br />
                                built for couples.
                            </BigHeader>
                        </>
                    }
                    items={[
                        {
                            icon: (
                                <LayersThree02
                                    color={CONNECT_ACCOUNT_COLOR}
                                    size="lg"
                                />
                            ),
                            text: (
                                <Paragraph color="textMuted">
                                    <Text
                                        as="span"
                                        color="black"
                                        fontWeight="semibold"
                                    >
                                        Money for two:
                                    </Text>{' '}
                                    one place to see where we are, where our
                                    money’s going, and to start investing
                                    towards our future, together.
                                </Paragraph>
                            ),
                        },
                        {
                            icon: (
                                <Toggle03Left
                                    color={CONNECT_ACCOUNT_COLOR}
                                    size="lg"
                                />
                            ),
                            text: (
                                <Paragraph color="textMuted">
                                    <Text
                                        as="span"
                                        color="black"
                                        fontWeight="semibold"
                                    >
                                        Toggle
                                    </Text>{' '}
                                    to easily see “what’s ours” and “what’s
                                    mine”. Flexibly share as much as you’d like,
                                    or keep other accounts private.
                                </Paragraph>
                            ),
                        },
                    ]}
                />
            )}
        </>
    );
};

export default DashboardPageWrapper;
